// shared/errorHandler.js

export const createErrorHandler = (contextName) => (error, operation = '') => {
  const message = `[${contextName}] ${operation ? operation + ': ' : ''}${error.message || 'An unexpected error occurred'}`;
  console.error(message, error);
  return {
    success: false,
    error: error.message || 'An unexpected error occurred'
  };
};

export const handleAsyncOperation = async (...args) => {
  // Handle both signatures:
  // 1. handleAsyncOperation(operation) - simple version for TournamentContext
  // 2. handleAsyncOperation(operation, { setLoading, setError, clearError }, errorHandler, operationName) - full version for ScrimContext
  
  if (args.length === 1) {
    // Simple version - just execute the operation
    const [operation] = args;
    try {
      const result = await operation();
      if (!result) {
        return { success: true };
      }
      
      // Don't spread arrays - return them directly
      if (Array.isArray(result)) {
        return result;
      }
      
      return result.success !== false ? { success: true, ...result } : result;
    } catch (error) {
      console.error('Async operation failed:', error);
      return {
        success: false,
        error: error.message || 'An unexpected error occurred'
      };
    }
  } else {
    // Full version with loading states and error handling.
    // Supports two shapes:
    //   a) (operation, { setLoading, setError, clearError }, errorHandlerFn, operationName)
    //   b) (operation, errorHandlerFn[, operationName])  — used widely by Tournament/Auth/Team contexts
    const operation = args[0];
    let setLoading, setError, clearError, errorHandlerFn, operationName = '';
    if (typeof args[1] === 'function') {
      errorHandlerFn = args[1];
      operationName = typeof args[2] === 'string' ? args[2] : '';
    } else {
      ({ setLoading, setError, clearError } = args[1] || {});
      errorHandlerFn = args[2];
      operationName = args[3] || '';
    }

    try {
      if (setLoading) setLoading(true);
      if (clearError) clearError();
      
      const result = await operation();
      
      if (setLoading) setLoading(false);
      
      // Handle undefined/null results properly
      if (!result) {
        return { success: true };
      }
      
      // Don't spread arrays - return them directly
      if (Array.isArray(result)) {
        return result;
      }
      
      return result.success !== false ? { success: true, ...result } : result;
    } catch (error) {
      if (setLoading) setLoading(false);
      
      // Don't show "Operation aborted" or "Operation cancelled" errors to users
      if (error.message === 'Operation aborted' || error.message === 'Operation cancelled') {
        return {
          success: false,
          error: error.message,
          silent: true // Mark as silent error that shouldn't be displayed
        };
      }
      
      const errorResult = typeof errorHandlerFn === 'function'
        ? errorHandlerFn(error, operationName)
        : { success: false, error: error?.message || 'An unexpected error occurred' };

      if (setError) {
        setError(errorResult.error);
      }

      return errorResult;
    }
  }
};
